import React, { useContext, useEffect, useRef, useState } from 'react';
import { store } from 'components/Store';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import normalizeString from 'components/Dashboard/utilities/normalizeString';
import KeyUpdateImage from './KeyUpdateImage';
import ReactGA from 'react-ga4';
import TagsLists from 'components/Dashboard/TagsList';
import useRememberPosition from 'components/Dashboard/hooks/useRememberPosition';
import createMarkup from 'components/Dashboard/helpers/createMarkup';
import getAppSite from 'utils/getAppSite';
import { useAuth0 } from '@auth0/auth0-react';
import time from 'utils/time';
import dayjs from 'dayjs';
import updateLocale from 'dayjs/plugin/updateLocale';
import GeneralItemActions from 'components/Dashboard/ItemController/GeneralItemActions';
import usePositionBodySnippet from 'components/Dashboard/hooks/usePositionBodySnippet';
import ContentTypeBadge from 'components/Dashboard/components/ContentTypeBadge';
import PriorityBadge from 'components/Dashboard/ItemController/PriorityBadge';
import {
  ItemControllerActions,
  ListItemContainer,
  ListItemContent,
  ListItemHeader,
  ListItemLink,
} from 'components/Dashboard/ItemController/ListItemLayout';
import useListItemController from 'components/Dashboard/ListControl/useListItemController';
import { flattenListRowProps } from 'components/Dashboard/ListControl/listRowProps';
import preventLink from 'components/Dashboard/TagsList/prevenLink';
dayjs.extend(updateLocale);
dayjs.updateLocale('en', {
  monthsShort: ['Jan', 'Feb', 'March', 'April', 'May', 'June', 'July', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'],
});

const ItemNew = ({ listRow }) => {
  const { listContext = {}, feedOverrides = {}, display = {}, interactions = {}, external = {} } = listRow ?? {};
  const { item, index, array, scrollBarRef, zIndex, listView, openInNewTab, nameSection } = listContext;
  const { explanations, feedItemFlags: propsFeedItemFlags } = feedOverrides;
  const {
    notShowDescriptionAndShare,
    switchRelevantExtractOrSummary,
    hideYellowDot,
    showContentTypeBadge,
    hideImage,
    tagsFixedPosition,
    onlyEmbargoedTag,
    hideOneSentenceSummary,
    tooltipText,
    notShowShare,
  } = display;
  const { itemOnclick, thumbDownProps, itemActionsProps, showPriorityBadge, setActiveUpdate } = interactions;
  const { embedShowTags, itemFromWidget } = external;
  const tagsListsProps = flattenListRowProps(listRow);

  const globalState = useContext(store);
  const { state } = globalState;
  const { contentResults } = useContentResults();
  const { search } = state;

  const { list, activeSearch } = search;
  const { KeyUpdates } = contentResults;
  const feedItemFlags = propsFeedItemFlags ?? KeyUpdates?.feedItemFlags ?? [];
  const { rememberPosition } = useRememberPosition();

  const { isAuthenticated } = useAuth0();

  const imageCondition = !itemFromWidget && isAuthenticated && !hideImage;

  const { unseenConditional, locationDescriptor, priorityBadgeCondition, rateUpdatesMode } = useListItemController({
    item,
    index,
    feedItemFlags: propsFeedItemFlags,
    itemFromWidget,
    hideYellowDot,
    thumbDownProps,
    showPriorityBadge,
    itemName: 'KeyUpdate',
  });

  return (
    <>
      <ListItemLink
        to={locationDescriptor(`/key-updates/${item.id}/${normalizeString(item.title)}`)}
        style={{ zIndex: zIndex ?? (array ? array.length - index : 1) }}
        target={openInNewTab ? '_blank' : undefined}
        rel={openInNewTab ? 'noopener noreferrer' : undefined}
        onClick={(e) => {
          if (rateUpdatesMode && setActiveUpdate) {
            preventLink(e);
            setActiveUpdate({ ...item, importanceRating: feedItemFlags?.[index]?.importanceRating });
            return;
          }
          if (scrollBarRef) {
            rememberPosition('KeyUpdates', scrollBarRef, index);
          }
          if (itemOnclick) {
            itemOnclick(e);
          }
          if (item.splitFilterNoActivity) {
            ReactGA.event({
              category: 'Blank Stakeholder Key Update Click',
              action: `${item.splitFilterNoActivity} click`,
              label: item.contentUrl,
            });
          }
        }}
        tooltipText={tooltipText}
      >
        <ListItemContainer className='my-2 content-item' unseen={unseenConditional}>
          <ListItemContent className='py-3 px-4'>
            <ListItemHeader
              className='mb-2'
              mainContent={
                showContentTypeBadge ? (
                  <div className='mt-1 specific-bottom-margin-4 flex-grow-1 text-light-blue blue-icon content-type-badge'>
                    <ContentTypeBadge objectType={item.type} />
                  </div>
                ) : (
                  <h4 className={`mb-0 flex-grow-1 content-item-title`}>{item.title}</h4>
                )
              }
              actions={
                !notShowShare ? (
                  <ItemControllerActions
                    rateUpdatesMode={rateUpdatesMode}
                    ratingFeedback={feedItemFlags?.[index]?.importanceRating}
                    priorityBadge={
                      priorityBadgeCondition ? <PriorityBadge item={item} feedItemFlags={feedItemFlags[index]} /> : null
                    }
                    actions={
                      <GeneralItemActions
                        item={item}
                        index={index}
                        nameSection={nameSection}
                        thumbDownProps={thumbDownProps}
                        itemActionsProps={itemActionsProps}
                      />
                    }
                  />
                ) : null
              }
            />
            {showContentTypeBadge && (
              <div className='flex-centered mb-2'>
                <h4 className={`mb-0 flex-grow-1 content-item-title`}>{item.title}</h4>
              </div>
            )}
            <div className='row mx-0 align-items-start'>
              <div className={`content-title flex-grow-1 ${onlyEmbargoedTag ? '' : 'pr-lg-5'} pr-2`}>
                {!notShowDescriptionAndShare && (
                  <>
                    <RelevantExtract
                      item={item}
                      listView={listView}
                      switchRelevantExtractOrSummary={switchRelevantExtractOrSummary}
                      hideOneSentenceSummary={hideOneSentenceSummary}
                    />
                  </>
                )}

                <p className='heading-date flex-grow-1 px-0 mb-0'>
                  {getAppSite() === 'usa' && (
                    <>
                      {(item.source || item.sourceOrganisation) && (
                        <>
                          <span className='paragraph-p4'>
                            {item.source ? new URL(item.source)?.hostname : item.sourceOrganisation}
                          </span>
                          <span className='dot'></span>
                        </>
                      )}
                      {item.sourceTags && (
                        <>
                          {item?.sourceTags?.map((sourceTag, index) => (
                            <React.Fragment key={index}>
                              <span className='paragraph-p4'>{sourceTag}</span>
                              <span className='dot'></span>
                            </React.Fragment>
                          ))}
                        </>
                      )}
                    </>
                  )}
                  <span
                    className='date'
                    data-tooltip-id='general-tooltip'
                    data-tooltip-content={
                      feedItemFlags[index]?.addedToFeedUtc
                        ? `Added to your feed on ${time(feedItemFlags[index]?.addedToFeedUtc).formatGeneralDate(true)}`
                        : undefined
                    }
                  >
                    {time(item.dateTime).formatGeneralDate()}
                  </span>
                  {getAppSite() !== 'usa' && (
                    <>
                      <span className='dot'></span>
                      {time(item.dateTime).formatTime()}
                    </>
                  )}
                </p>

                {((list !== null && activeSearch && !itemFromWidget) || embedShowTags) && (
                  <TagsLists
                    {...tagsListsProps}
                    explanations={explanations ?? KeyUpdates?.explanations}
                    feedItemFlags={feedItemFlags}
                    index={index}
                    section={'KeyUpdates'}
                    embargoed={item.embargoed}
                    embargoedUntil={item.embargoedUntil}
                    tagsFixedPosition={tagsFixedPosition}
                    scrollBarRef={scrollBarRef}
                    onlyEmbargoedTag={onlyEmbargoedTag}
                  />
                )}
              </div>
              {imageCondition && <KeyUpdateImage item={item} />}
            </div>
          </ListItemContent>
        </ListItemContainer>
      </ListItemLink>
    </>
  );
};

const RelevantExtract = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { sectionsView } = state;

  const { item, listView, switchRelevantExtractOrSummary, hideOneSentenceSummary } = props;
  const { oneSentenceSummary } = item ?? {};
  const [heightItem, setHeightItem] = useState(0);
  const [translateValue, setTranslateValue] = useState(0);
  const [animated, setAnimated] = useState();

  const { selectedOptionRelevantExtract } = sectionsView ?? {};
  const [localSelectedOption, setLocalSelectedOption] = useState(selectedOptionRelevantExtract);
  const sentenceSummaryComponent = useRef(null);
  const titleBodyComponent = useRef(null);

  const isMountedComponent = useRef(false);
  const oneAiSummary = selectedOptionRelevantExtract === 'One-line AI summary';

  const { bodySnippet } = usePositionBodySnippet({ titleBodyComponent, item });

  useEffect(() => {
    const createHeight = () => {
      let bodyElement = titleBodyComponent?.current;
      let sentenceElement = sentenceSummaryComponent?.current;

      let elHeight = selectedOptionRelevantExtract === 'Most relevant extract' ? bodyElement : sentenceElement;
      setTranslateValue(oneAiSummary ? bodyElement?.getBoundingClientRect()?.height + 7.5 : 0);
      setHeightItem(elHeight?.getBoundingClientRect()?.height + 7.5);
    };
    const resizeObserver = new ResizeObserver(createHeight);
    [titleBodyComponent.current, sentenceSummaryComponent.current].forEach((el) => el && resizeObserver.observe(el));
    setTimeout(() => {
      isMountedComponent.current = true;
    }, 100); //value while the text gets in the correct position
    return () => resizeObserver.disconnect();
  }, [selectedOptionRelevantExtract]);

  useEffect(() => {
    if (selectedOptionRelevantExtract !== localSelectedOption) {
      setLocalSelectedOption(selectedOptionRelevantExtract);
      setTimeout(() => {
        setAnimated(true);
        setTimeout(() => {
          setAnimated(false);
        }, 1000); //value of shimmer animation
      }, 500); //Value of dropdown animation
    }
  }, [selectedOptionRelevantExtract]);

  return (
    <div
      className='overflow-hidden relevant-extract-keyupdates'
      style={{
        height: `${!!oneSentenceSummary && switchRelevantExtractOrSummary ? `${heightItem}px` : 'auto'}`,
      }}
    >
      <p
        className={`${listView ? 'title-body-before position-relative' : ''} ${
          isMountedComponent.current && listView ? 'title-body-transition' : ''
        } mb-2 title-body ${animated ? 'title-body-animated' : ''}`}
        style={
          !!oneSentenceSummary && listView
            ? {
                transform: `translateY(-${translateValue}px)`,
                opacity: oneAiSummary ? 0 : undefined,
                transition: oneAiSummary ? 'all 500ms' : undefined,
              }
            : null
        }
        ref={titleBodyComponent}
        dangerouslySetInnerHTML={createMarkup(bodySnippet)}
      />
      {!!oneSentenceSummary && switchRelevantExtractOrSummary && !hideOneSentenceSummary && (
        <p
          className={`${listView ? 'title-body-before' : ''} ${
            isMountedComponent.current ? 'title-body-transition' : ''
          } title-body ${animated ? 'title-body-animated' : ''} ${!(!!oneSentenceSummary && listView) ? 'mb-2' : ''}`}
          style={
            !!oneSentenceSummary && listView
              ? {
                  transform: `translateY(-${translateValue}px)`,
                }
              : null
          }
          ref={sentenceSummaryComponent}
          dangerouslySetInnerHTML={createMarkup(oneSentenceSummary)}
        />
      )}
    </div>
  );
};

export default ItemNew;
